
import { defineComponent, onBeforeMount, computed, ref } from "vue";
import { useStore } from "vuex";

import TravelService from "@/services/travel.service";

import SearchDatepicker from "@/components/Search/SearchDatepicker.vue";
import SearchTravellers from "@/components/Search/SearchTravellers.vue";
import SearchAirports from "@/components/Search/SearchAirports.vue";

import IResponseData from "@/types/IResponseData";
import { SearchAirport, SearchTravelRequest } from "@/types/models";
import applicationService from "@/services/application.service";

export default defineComponent({
  name: "SearchComponentHotel",
  components: {
    SearchDatepicker,
    SearchTravellers,
    SearchAirports,
  },
  setup() {
    const store = useStore();

    const showNewSearchWarning = ref(false);

    const pageUrls = computed(() => store.getters["ApplicationModule/getPageUrls"]);
    const pageTexts = computed(() => store.getters["ApplicationModule/getPageTexts"]);
    const localeStrings = computed(() => store.getters["ApplicationModule/getLocaleStrings"]);

    const travellersDropdown = ref();

    const searchTravelRequest = computed(
      () => store.getters["TravelSearchModule/getSearchTravelRequest"]
    )

    const adultsCount = computed(() => searchTravelRequest.value.rooms.reduce((acc, { adults }) => acc + adults.length, 0));
    const childrenCount = computed(() => searchTravelRequest.value.rooms.reduce((acc, { children }) => acc + children.length, 0));
    const paxCount = computed(() => adultsCount.value + childrenCount.value);

    const closeSearchWarningModal = () => {
      showNewSearchWarning.value = false;
    };

    const searchbarHeadline = computed(() => {
      return pageTexts.value['searchbarHeadlineHotel'].replace(/\[[hotel\]]+\]/g, document.querySelector('h1')?.innerText);
    })

    const onModalOpen = () => {
      document.body.classList.add('modal-active');
    }

    const onModalClose = () => {
      document.body.classList.remove('modal-active');
    }

    const allChildrenHasAge = () => {
      for (const room of searchTravelRequest.value.rooms) {
        for (const child of room.children) {
          if (child.age === null) return false;
        }
      }
      return true;
    };

    const paxLimit = () => {
      if (paxCount.value > 9) return false;
      return true;
    };

    const doSearch = () => {
      if (allChildrenHasAge() && paxLimit()) {
        store.dispatch("TravelSearchResponseModule/deleteSearchTravelResponse");
        store.dispatch("TravelSearchBookingModule/deleteSearchTravelBookingOverview");
        store.dispatch("ApplicationModule/resetAccommodationIndex");
        store.dispatch("ApplicationModule/resetPensionIndex");
        store.dispatch("ApplicationModule/resetPersonalDataIndex");
        const hotelName =  document.getElementsByTagName('h1')[0] as HTMLElement;
        window.location.href = window.location.origin + pageUrls.value.pageHotelList + '?' + encodeURIComponent(hotelName.innerText);
      }
      if (!allChildrenHasAge()) {
        setMissingChildAgeError()
      }

      if (!paxLimit()) {
        setPaxLimitError()
      }
    };

    onBeforeMount(() => {
      if (!store.getters["ApplicationModule/getPageUrls"]) {
        applicationService
          .getPageUrls()
          .then((response: IResponseData<{ [key: string]: string }>) => {
            store.dispatch("ApplicationModule/updatePageUrls", response.data);
          });
      }

      if (!store.getters["ApplicationModule/getPageTexts"]) {
        applicationService
          .getPageTexts()
          .then((response: IResponseData<{ [key: string]: string }>) => {
            store.dispatch(
              "ApplicationModule/updatePageTexts",
              response.data
            );
          });
      }

      store.dispatch(
        "ApplicationModule/updateLocaleStrings",
        document.documentElement.getAttribute('lang')
      );

      if (!store.getters["TravelSearchModule/getSearchTravelRequest"]) {
        TravelService.getDefaultSearchRequest().then(
          (response: IResponseData<SearchTravelRequest>) => {
            store.dispatch("TravelSearchModule/updateSearchTravelRequest", response.data.result);
          }
        );
      }
    });

    const updateFlexibleDate = (flexDate: number) => {
      store.dispatch(
        "TravelSearchModule/updateSearchTravelRequestFlexibleDepartureDays",
        flexDate
      );
    };

    const updateDate = (dateFrom: string, dateTo: string) => {
      store.dispatch("TravelSearchModule/updateSearchTravelRequestDateFrom", dateFrom);
      store.dispatch("TravelSearchModule/updateSearchTravelRequestDateTo", dateTo);
    };

    const updateAirports = (airports: Array<SearchAirport>) => {
      store.dispatch("TravelSearchModule/updateSearchTravelRequestAirports", airports);
    };

    const setMissingChildAgeError = () => {
      const clickEvent = new Event('click');
      document.getElementById('searchTravellersBtn')?.dispatchEvent(clickEvent);
      travellersDropdown.value.allChildrenHasAge();
    }
    
    const setPaxLimitError = () => {
      const clickEvent = new Event('click');
      document.getElementById('searchTravellersBtn')?.dispatchEvent(clickEvent);
      travellersDropdown.value.paxLimit()
    }

    const searchTravel = () => {
      if (store.getters["TravelSearchResponseModule/getSearchTravelResponse"]?.travels?.length) {
        if (paxLimit()) {
          showNewSearchWarning.value = true;
        } else {
          setPaxLimitError();
        }
        if (allChildrenHasAge()) {
          showNewSearchWarning.value = true;
        } else {
          setMissingChildAgeError();
        }
      } else {
        doSearch();
      }
    };


    return {
      searchTravelRequest,
      updateDate,
      updateAirports,
      updateFlexibleDate,
      searchTravel,
      showNewSearchWarning,
      closeSearchWarningModal,
      onModalOpen,
      onModalClose,
      doSearch,
      pageTexts,
      searchbarHeadline,
      localeStrings
    };
  },
});
